import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, Paper, Stack, Typography, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PageLayout from '../shell/PageLayout';
import { useOperations } from './OperationsProvider';
import { EmptyState, StaleNotice } from './FeedState';
import useOperationalPermissions from '../common/util/operationalPermissions';
import { awaitingReturn } from './devices';
import { humanMs } from './areas';
import { formatDateTime } from '../common/util/datetime';
import AdmitTruck from './AdmitTruck';
import { challanOwed, realShipment } from '../trips/challan';

// Devices come back to the counter that issued them, so outstanding returns sit beside intake.
const ReturnsPanel = ({ data, mutate }) => {
  const { updatedAt, error: unavailable } = useOperations();
  const [pending, setPending] = useState(null); const [error, setError] = useState(null);
  const rows = awaitingReturn(data.assignments, data.sources);
  const nameOf = (list, id, key) => list.find((row) => row.id === id)?.[key] || 'Unknown';
  const markReturned = async (assignmentId) => {
    setPending(assignmentId); setError(null);
    try { await mutate(`assignments/${assignmentId}/return`, {}); } catch (e) { setError(e.message); } finally { setPending(null); }
  };
  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Stack spacing={1.5}>
        <Typography variant="subtitle1" fontWeight={700}>{`Devices awaiting return (${rows.length})`}</Typography>
        {error && <Alert severity="error">{error}</Alert>}
        <StaleNotice updatedAt={updatedAt} unavailable={unavailable} />
        {/* "Every checked-out device is back" is a statement about the plant. Before anything has
            loaded, or when the feed is down, the honest statement is that nobody knows - a counter
            that reads a confident all-clear off an empty array is how a device goes missing. */}
        {!rows.length && <EmptyState updatedAt={updatedAt} unavailable={unavailable} subject="Device custody" empty="Every checked-out device is back." />}
        {rows.map((a) => (
          <Stack key={a.id} direction="row" justifyContent="space-between" alignItems="center" gap={1}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600}>{`${nameOf(data.sources, a.sourceId, 'identifier')} · ${a.kind}`}</Typography>
              <Typography variant="caption" color="text.secondary" component="div">{nameOf(data.vehicles, a.vehicleId, 'name')}</Typography>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ whiteSpace: 'nowrap' }}>{`Released ${formatDateTime(a.endedAt)}`}</Typography>
            </Box>
            <Button size="small" disabled={pending != null || !!unavailable || !updatedAt} onClick={() => markReturned(a.id)}>{pending === a.id ? 'Saving…' : 'Returned'}</Button>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
};

// What this desk admitted and has not yet handed to a gate. The queue on the Command Center is
// geometric - it lists whoever is standing inside a parking boundary - which answers a different
// question and says nothing when a truck has no fix yet or the site drew no parking area. A counter
// needs its own work in front of it, longest wait first, so admitting a truck does not end in it
// disappearing from the screen.
export const waitingAtYard = (trips) => trips
  .filter((t) => !t.closedAt && t.phase === 'AT_YARD')
  .map((t) => ({ ...t, waitingMs: Date.parse(t.yardAt || t.createdAt) ? Date.now() - Date.parse(t.yardAt || t.createdAt) : null }))
  .sort((a, b) => (b.waitingMs ?? -1) - (a.waitingMs ?? -1));

const WaitingPanel = ({ trips }) => {
  const navigate = useNavigate();
  const { updatedAt, error: unavailable } = useOperations();
  const rows = waitingAtYard(trips);
  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Stack spacing={1.5}>
        <Typography variant="subtitle1" fontWeight={700}>{`Waiting in the parking (${rows.length})`}</Typography>
        <StaleNotice updatedAt={updatedAt} unavailable={unavailable} />
        {!rows.length && <EmptyState updatedAt={updatedAt} unavailable={unavailable} subject="The parking queue" empty="Nothing is waiting to be called in from this counter." />}
        {rows.map((t) => (
          <Stack key={t.id} direction="row" justifyContent="space-between" alignItems="center" gap={1}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600}>{t.vehicle}</Typography>
              {/* A lorry parked on its number plate alone carries a placeholder rather than a
                  number, and printing the placeholder tells the desk nothing it can use. What it
                  needs to know is that the paper is outstanding. */}
              <Typography variant="caption" color="text.secondary" component="div">
                {realShipment(t) || t.challanNumber ? `Challan ${t.challanNumber || realShipment(t)}` : 'Challan to follow'}
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div">{t.transporter}</Typography>
            </Box>
            <Stack alignItems="flex-end" gap={0.5}>
              <Chip size="small" label={humanMs(t.waitingMs)} variant="outlined" />
              {challanOwed(t).length > 0 && <Chip size="small" color="warning" variant="outlined" label={`${challanOwed(t).length} to enter`} />}
              <Button size="small" onClick={() => navigate(`/trips/${t.id}`)}>Open</Button>
            </Stack>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
};

// The parking counter: register the truck if it is new, check its challan against the imported
// order, and issue a portable device unless the truck carries its own.
const YardIntakePage = () => {
  const { data, trips, mutate } = useOperations();
  const { operate } = useOperationalPermissions();
  const navigate = useNavigate();
  const theme = useTheme(); const narrow = useMediaQuery(theme.breakpoints.down('lg'));
  // Admitting one truck is not the end of the shift, so the counter keeps its form and gains a row
  // in the queue beside it, rather than being thrown onto that truck's trip page.
  const [admitted, setAdmitted] = useState(null);
  if (!operate) return <PageLayout title="Parking intake"><Alert severity="info">Plant Operator permission is required.</Alert></PageLayout>;
  const panels = (
    <>
      <WaitingPanel trips={trips} />
      <ReturnsPanel data={data} mutate={mutate} />
    </>
  );
  return (
    <PageLayout title="Parking intake" subtitle="Register the truck at the parking, check its challan, and issue a portable GPS device unless it carries its own.">
      {admitted && (
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          onClose={() => setAdmitted(null)}
          action={<Button size="small" sx={{ whiteSpace: 'nowrap' }} onClick={() => navigate(`/trips/${admitted.id}`)}>Open trip</Button>}
        >
          {`${admitted.vehicle} is admitted and waiting in the parking. The next truck can be entered below.`}
        </Alert>
      )}
      <AdmitTruck desk="YARD" onAdmitted={setAdmitted} sidebar={!narrow ? panels : null} />
      {narrow && (
      <Box component="details" sx={{ mt: 2 }}>
        <Typography component="summary" sx={{ cursor: 'pointer', py: 2 }}>Yard queue and device returns</Typography>
        <Stack spacing={2}>{panels}</Stack>
      </Box>
      )}
    </PageLayout>
  );
};
export default YardIntakePage;
